import { Button, ButtonPalette, RadioButtonGroup } from 'base/ui/button';
import { Checkbox } from 'base/ui/checkbox';
import { CodeBlock } from 'base/ui/codeblock';
import { Dialog } from 'base/ui/dialog';
import { IconExternalLink } from 'base/ui/icons';
import { Rows } from 'base/ui/layout';
import { Link } from 'base/ui/link';
import { TickRow } from 'base/ui/tick_row';
import {
  TextRegularLight,
  TextSmallLight,
  TextTinyLight,
} from 'base/ui/typography';
import { DialogButtons } from 'blocks/auth/accept_plan_offer';
import React from 'react';
import styles from './export_react_dialog.css';

function Keyword({ children }: { children: string }) {
  return <span className={styles.highlightKeyword}>{children}</span>;
}

function Constant({ children }: { children: string }) {
  return <span className={styles.highlightConstant}>{children}</span>;
}

const tailwindConfigJs = `// Tailwind.config.js
module.exports = {
  content: ["./node_modules/@relume_io/relume-ui/dist/**/*.{js,ts,jsx,tsx}"],
  presets: [require("@relume_io/relume-tailwind")],
};
`;

const styledTailwindConfigJs = [
  '// Tailwind.config.js\n',
  'module.exports = {\n',
  '  content: [\n',
  '    ',
  <Constant key="1">
    {'"./node_modules/@relume_io/relume-ui/dist/**/*.{js,ts,jsx,tsx}"'}
  </Constant>,
  ',\n',
  '    ',
  <Constant key="2">{'"./app/**/*.{js,ts,jsx,tsx,mdx}"'}</Constant>,
  ',\n',
  '    ',
  <Constant key="3">{'"./src/**/*.{js,ts,jsx,tsx,mdx}"'}</Constant>,
  ',\n',
  '  ],\n',
  '  presets: [',
  <Keyword key="4">require</Keyword>,
  '(',
  <Constant key="5">"@relume_io/relume-tailwind"</Constant>,
  ')],\n',
  '};\n',
];

const tailwindConfigTs = `// tailwind.config.ts
import type { Config } from "tailwindcss";
import relumeTailwindPreset from "@relume_io/relume-tailwind";

const config: Config = {
  content: [
    "./node_modules/@relume_io/relume-ui/dist/**/*.{js,ts,jsx,tsx}",
    "./app/**/*.{js,ts,jsx,tsx,mdx}",
    "./src/**/*.{js,ts,jsx,tsx,mdx}",
  ],
  presets: [relumeTailwindPreset]
};
export default config;
`;

const styledTailwindConfigTs = [
  '// tailwind.config.ts\n',
  <Keyword key="1">import type</Keyword>,
  ' { Config } ',
  <Keyword key="2">from</Keyword>,
  ' ',
  <Constant key="3">"tailwindcss"</Constant>,
  ';\n\n',

  <Keyword key="4">import</Keyword>,
  ' ',
  'relumeTailwindPreset',
  ' ',
  <Keyword key="5">from</Keyword>,
  ' ',
  <Constant key="6">"@relume_io/relume-tailwind"</Constant>,
  ';\n\n',

  <Keyword key="7">const</Keyword>,
  ' config: Config = {\n',
  '  content: [\n',
  '    ',
  <Constant key="8">
    {'"./node_modules/@relume_io/relume-ui/dist/**/*.{js,ts,jsx,tsx}"'}
  </Constant>,
  ',\n',
  '    ',
  <Constant key="9">{'"./app/**/*.{js,ts,jsx,tsx,mdx}"'}</Constant>,
  ',\n',
  '    ',
  <Constant key="10">{'"./src/**/*.{js,ts,jsx,tsx,mdx}"'}</Constant>,
  ',\n',
  '  ],\n',
  '  presets: [relumeTailwindPreset]\n',
  '};\n',
  <Keyword key="11">export default</Keyword>,
  ' config;\n',
];

export function hasDismissedReactExportInstructions() {
  return localStorage.getItem('dismissedReactExportInstructions') === 'true';
}

function dismissedReactExportInstructions() {
  localStorage.setItem('dismissedReactExportInstructions', 'true');
}

const ReactDocUrl = 'https://react-docs.relume.io/';
export function ExportReactDialog({
  onClose,
  showCopyConfirmation,
}: {
  onClose(): void;
  showCopyConfirmation?(): void;
}) {
  const [visible] = React.useState(!hasDismissedReactExportInstructions());
  const [checked, setChecked] = React.useState(false);
  const [configStyle, setConfigStyle] = React.useState<'js' | 'ts'>('js');
  if (!visible) {
    return null;
  }
  const onConfirm = () => {
    if (checked) {
      dismissedReactExportInstructions();
    }
    onClose();
  };
  return (
    <Dialog title="Getting Started with React" onClose={onClose}>
      <div className={styles.exportHelpDialogContainer}>
        <Rows spacing={32}>
          <Rows spacing={24}>
            <TextRegularLight>
              Follow these instructions to setup your project.
            </TextRegularLight>
            <Rows spacing={16}>
              <Rows spacing={8}>
                <TickRow type="one">Install packages</TickRow>
                <CodeBlock
                  onCopy={showCopyConfirmation}
                  code={
                    'npm i @relume_io/relume-ui @relume_io/relume-tailwind\n'
                  }
                />
              </Rows>
              <Rows spacing={8}>
                <TickRow type="two">Update Tailwind config</TickRow>
                <ButtonPalette>
                  <RadioButtonGroup
                    options={[
                      { label: 'Javascript', value: 'js' },
                      { label: 'Typescript (ESM)', value: 'ts' },
                    ]}
                    onChange={setConfigStyle}
                    value={configStyle}
                  />
                </ButtonPalette>
                {configStyle === 'js' ? (
                  <CodeBlock
                    key="js"
                    code={tailwindConfigJs}
                    onCopy={showCopyConfirmation}
                  >
                    {styledTailwindConfigJs}
                  </CodeBlock>
                ) : (
                  <CodeBlock
                    key="ts"
                    code={tailwindConfigTs}
                    onCopy={showCopyConfirmation}
                  >
                    {styledTailwindConfigTs}
                  </CodeBlock>
                )}
                <TextTinyLight color="grey">
                  View the entire{' '}
                  <TextTinyLight tag="span" color="black">
                    <Link href="https://react-docs.relume.io/getting-started/tailwind">
                      Tailwind config
                    </Link>
                  </TextTinyLight>
                </TextTinyLight>
              </Rows>
            </Rows>
          </Rows>
          <Rows spacing={24}>
            <Rows spacing={4}>
              <Checkbox
                label="Don’t show this again"
                size="large"
                value={checked}
                onChange={setChecked}
              />
              {checked && (
                <TextSmallLight color="grey">
                  {'You can find these instructions at any time in our '}
                  <Link href={ReactDocUrl}>React Docs</Link>.
                </TextSmallLight>
              )}
            </Rows>
            <DialogButtons>
              <Button
                variant="secondary"
                label="React Docs"
                iconRight={IconExternalLink}
                target="_blank"
                href={ReactDocUrl}
                size="large"
              />
              <Button
                variant="primary"
                label="Got it"
                onClick={onConfirm}
                size="large"
              />
            </DialogButtons>
          </Rows>
        </Rows>
      </div>
    </Dialog>
  );
}
